import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, HeadContent, Scripts } from "@tanstack/react-router";
import { getPreferencesServerFn, PreferencesProvider } from "@/components/preferences-provider";
import { getThemeServerFn, ThemeProvider } from "@/components/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { ClientHintCheck, getClientHints } from "@/lib/client-hints";
import appCss from "../styles.css?url";

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			{
				title: "Podcast Backoffice",
			},
		],
		links: [
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "96x96",
				href: "/favicon-96x96.png",
			},
			{ rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
			{ rel: "icon", href: "/favicon.ico" },
		],
	}),
	shellComponent: RootDocument,
	loader: async () => {
		const theme = await getThemeServerFn();
		const preferences = await getPreferencesServerFn();

		return {
			preferences,
			requestInfo: {
				hints: getClientHints(),
			},
			theme,
		};
	},
});

function RootDocument({ children }: { children: React.ReactNode }) {
	const { theme, requestInfo } = Route.useLoaderData();

	return (
		<html lang="en" className={theme || requestInfo.hints.theme}>
			<head>
				<ClientHintCheck />
				<HeadContent />
			</head>
			<body>
				<ThemeProvider>
					<PreferencesProvider>{children}</PreferencesProvider>

					<Toaster />
				</ThemeProvider>
				<Scripts />
			</body>
		</html>
	);
}
